import React, { useState, useEffect, useCallback } from 'react';
import { Paper, Box, TextField, Button, TableHead, TableRow, TableCell, TableBody, Typography, Grid, Divider, CircularProgress, Chip, ButtonGroup, Card, CardContent, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import axios from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount as formatPlain } from '../../utils/money';
import { toast } from 'react-toastify';
import ScrollTable, { EMBEDDED_MAX_HEIGHT } from '../ScrollTable';


const Reports = () => {
  const [section, setSection] = useState(0); // 0 = Accounting, 1 = Operations
  const [tab, setTab] = useState(0);
  const [occupancy, setOccupancy] = useState({ total: 0, occupied: 0, available: 0, maintenance: 0, occupancyRate: 0 });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedLandlord, setSelectedLandlord] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('');
  const [landlords, setLandlords] = useState([]);
  const [properties, setProperties] = useState([]);
  const [trialBalance, setTrialBalance] = useState([]);
  const [loadingTB, setLoadingTB] = useState(false);
  const [profitLoss, setProfitLoss] = useState({ totalIncome: 0, totalExpense: 0, netIncome: 0 });
  const [cashFlow, setCashFlow] = useState({ inflow: 0, outflow: 0, net: 0, transactions: [] });
  const [agencyFees, setAgencyFees] = useState({ totalRevenue: 0, byLandlord: [] });
  const [loadingAgencyFees, setLoadingAgencyFees] = useState(false);
  const { formatMoney: formatCurrency } = useCurrency();

  const fetchLandlords = async () => {
    try {
      const res = await axios.get('/landlords');
      setLandlords(res.data || []);
    } catch (error) {
      console.error('Error fetching landlords:', error);
    }
  };

  const fetchPropertiesByLandlord = useCallback(async (landlordId) => {
    if (!landlordId) {
      setProperties([]);
      setSelectedProperty('');
      return;
    }
    try {
      console.log('Fetching properties for landlord:', landlordId);
      // Try the landlords endpoint first
      let res;
      try {
        res = await axios.get(`/landlords/${landlordId}/properties`);
      } catch (err) {
        // Fallback: use properties endpoint with filter
        console.log('Trying fallback endpoint...');
        res = await axios.get('/properties', {
          params: { landlord: landlordId }
        });
        // If it returns an array, use it directly
        if (Array.isArray(res.data)) {
          setProperties(res.data);
          return;
        }
        throw err;
      }
      console.log('Properties response:', res.data);
      setProperties(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch properties';
      toast.error(errorMessage);
      setProperties([]);
    }
  }, []);

  useEffect(() => {
    fetchLandlords();
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPropertiesByLandlord(selectedLandlord);
  }, [selectedLandlord, fetchPropertiesByLandlord]);

  
  // No prefix — the ledger tables below carry the currency in their heading.
  const formatAmount = (n) => formatPlain(n);

  const fetchAll = async () => {
    await Promise.all([fetchTrialBalance(), fetchProfitLoss(), fetchCashFlow(), fetchOccupancy(), fetchRepairs(), fetchPaymentsRpt()]);
  };

  const buildParams = () => {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (selectedLandlord) params.landlordId = selectedLandlord;
    if (selectedProperty) params.propertyId = selectedProperty;
    return params;
  };

  const fetchTrialBalance = async () => {
    try {
      setLoadingTB(true);
      const res = await axios.get('/accounting/reports/trial-balance', { params: buildParams() });
      setTrialBalance(res.data || []);
    } catch (e) {
      toast.error('Failed to load trial balance');
    } finally { setLoadingTB(false); }
  };

  const [loadingPL, setLoadingPL] = useState(false);
  const fetchProfitLoss = async () => {
    try {
      setLoadingPL(true);
      const res = await axios.get('/accounting/reports/profit-loss', { params: buildParams() });
      setProfitLoss(res.data || { totalIncome: 0, totalExpense: 0, netIncome: 0 });
    } catch (e) {
      toast.error('Failed to load profit & loss');
    } finally { setLoadingPL(false); }
  };

  const [loadingOcc, setLoadingOcc] = useState(false);
  const fetchOccupancy = async () => {
    try {
      setLoadingOcc(true);
      const res = await axios.get('/reports/occupancy');
      setOccupancy(res.data || occupancy);
    } catch (e) { /* noop */ } finally { setLoadingOcc(false); }
  };

  const [repairsRpt, setRepairsRpt] = useState({ total: 0, byStatus: {}, records: [] });
  const [loadingRep, setLoadingRep] = useState(false);
  const fetchRepairs = async () => {
    try {
      setLoadingRep(true);
      const res = await axios.get('/reports/repairs', { params: buildParams() });
      setRepairsRpt(res.data || { total: 0, byStatus: {}, records: [] });
    } catch (e) { /* noop */ } finally { setLoadingRep(false); }
  };

  const [paymentsRpt, setPaymentsRpt] = useState({ totalAmount: 0, totalCount: 0, byStatus: {} });
  const [loadingPay, setLoadingPay] = useState(false);
  const fetchPaymentsRpt = async () => {
    try {
      setLoadingPay(true);
      const res = await axios.get('/reports/payments', { params: buildParams() });
      setPaymentsRpt(res.data || { totalAmount: 0, totalCount: 0, byStatus: {} });
    } catch (e) { /* noop */ } finally { setLoadingPay(false); }
  };

  const fetchCashFlow = async () => {
    try {
      // The direct method: this hub lists the movements themselves. Without `method` the
      // endpoint now answers with the IAS 7 indirect statement, which has no transactions.
      const res = await axios.get('/accounting/reports/cash-flow', { params: { ...buildParams(), method: 'direct' } });
      setCashFlow(res.data || { inflow: 0, outflow: 0, net: 0, transactions: [] });
    } catch (e) {
      toast.error('Failed to load cash flow');
    }
  };

  const fetchAgencyFees = async () => {
    try {
      setLoadingAgencyFees(true);
      const res = await axios.get('/agency-reports/management-fee-revenue', { params: buildParams() });
      setAgencyFees(res.data || { totalRevenue: 0, byLandlord: [] });
    } catch (e) {
      toast.error('Failed to load agency fees');
    } finally {
      setLoadingAgencyFees(false);
    }
  };

  const handleApply = async () => {
    await fetchAll();
    if (section === 1 && tab === 3) {
      await fetchAgencyFees();
    }
  };

  const renderFilterCard = (showProperty = false) => (
    <Card sx={{ bgcolor: '#F8FAFC', borderRadius: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider', mb: 3 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
          Report Filters
        </Typography>
        <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Landlord</InputLabel>
            <Select
              value={selectedLandlord}
              label="Landlord"
              onChange={(e) => setSelectedLandlord(e.target.value)}
            >
              <MenuItem value="">All Landlords</MenuItem>
              {landlords.map((landlord) => (
                <MenuItem key={landlord._id} value={landlord._id}>
                  {landlord.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {showProperty && (
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={!selectedLandlord}>
              <InputLabel>Property</InputLabel>
              <Select
                value={selectedProperty}
                label="Property"
                onChange={(e) => setSelectedProperty(e.target.value)}
              >
                <MenuItem value="">All Properties</MenuItem>
                {properties.map((property) => (
                  <MenuItem key={property._id} value={property._id}>
                    {property.propertyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ flex: 1, maxWidth: 200 }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ flex: 1, maxWidth: 200 }}
          />
          <Button 
            variant="contained" 
            onClick={handleApply}
            sx={{ height: '40px', minWidth: 100 }}
          >
            Apply
          </Button>
          <Button 
            variant="outlined" 
            onClick={handleClearFilters}
            sx={{ height: '40px', minWidth: 100 }}
          >
            Clear
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const handleClearFilters = async () => {
    setStartDate('');
    setEndDate('');
    setSelectedLandlord('');
    setSelectedProperty('');
    // Wait a moment for state to update, then fetch with cleared filters
    setTimeout(async () => {
      await fetchAll();
      if (section === 1 && tab === 3) {
        await fetchAgencyFees();
      }
    }, 0);
  };

  return (
    <Box>
      {/* Primary Tabs - Separate Buttons */}
      <Box sx={{ mb: 4, display: 'flex', gap: 6, justifyContent: 'center' }}>
        <Button
          variant={section === 0 ? 'contained' : 'outlined'}
          onClick={() => { setSection(0); setTab(0); }}
          sx={{
            px: 5,
            py: 2,
            fontWeight: 800,
            textTransform: 'none',
            fontSize: '1.25rem',
            minWidth: 250,
            borderRadius: 2,
            boxShadow: section === 0 ? 2 : 0,
            '&:hover': {
              boxShadow: section === 0 ? 3 : 1,
            }
          }}
        >
          Accounting Reports
        </Button>
        <Button
          variant={section === 1 ? 'contained' : 'outlined'}
          onClick={() => { setSection(1); setTab(0); }}
          sx={{
            px: 5,
            py: 2,
            fontWeight: 800,
            textTransform: 'none',
            fontSize: '1.25rem',
            minWidth: 250,
            borderRadius: 2,
            boxShadow: section === 1 ? 2 : 0,
            '&:hover': {
              boxShadow: section === 1 ? 3 : 1,
            }
          }}
        >
          Operational Reports
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Secondary Tabs */}
      {section === 0 && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start', gap: 9 }}>
          <Button
            variant={tab === 0 ? 'text' : 'text'}
            onClick={() => setTab(0)}
            sx={{
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: tab === 0 ? 800 : 700,
              color: tab === 0 ? 'primary.main' : 'text.secondary',
              minHeight: 48,
              px: 3,
              borderBottom: tab === 0 ? '3px solid' : 'none',
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                bgcolor: 'transparent',
                color: 'primary.main',
              }
            }}
          >
            Trial Balance
          </Button>
          <Button
            variant={tab === 1 ? 'text' : 'text'}
            onClick={() => setTab(1)}
            sx={{
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: tab === 1 ? 800 : 700,
              color: tab === 1 ? 'primary.main' : 'text.secondary',
              minHeight: 48,
              px: 3,
              borderBottom: tab === 1 ? '3px solid' : 'none',
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                bgcolor: 'transparent',
                color: 'primary.main',
              }
            }}
          >
            Profit & Loss
          </Button>
          <Button
            variant={tab === 2 ? 'text' : 'text'}
            onClick={() => setTab(2)}
            sx={{
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: tab === 2 ? 800 : 700,
              color: tab === 2 ? 'primary.main' : 'text.secondary',
              minHeight: 48,
              px: 3,
              borderBottom: tab === 2 ? '3px solid' : 'none',
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                bgcolor: 'transparent',
                color: 'primary.main',
              }
            }}
          >
            Cash Flow
          </Button>
        </Box>
      )}

      {section === 1 && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start', gap: 9 }}>
          <Button
            variant={tab === 0 ? 'text' : 'text'}
            onClick={() => setTab(0)}
            sx={{
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: tab === 0 ? 800 : 700,
              color: tab === 0 ? 'primary.main' : 'text.secondary',
              minHeight: 48,
              px: 3,
              borderBottom: tab === 0 ? '3px solid' : 'none',
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                bgcolor: 'transparent',
                color: 'primary.main',
              }
            }}
          >
            Occupancy
          </Button>
          <Button
            variant={tab === 1 ? 'text' : 'text'}
            onClick={() => setTab(1)}
            sx={{
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: tab === 1 ? 800 : 700,
              color: tab === 1 ? 'primary.main' : 'text.secondary',
              minHeight: 48,
              px: 3,
              borderBottom: tab === 1 ? '3px solid' : 'none',
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                bgcolor: 'transparent',
                color: 'primary.main',
              }
            }}
          >
            Repairs
          </Button>
          <Button
            variant={tab === 2 ? 'text' : 'text'}
            onClick={() => setTab(2)}
            sx={{
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: tab === 2 ? 800 : 700,
              color: tab === 2 ? 'primary.main' : 'text.secondary',
              minHeight: 48,
              px: 3,
              borderBottom: tab === 2 ? '3px solid' : 'none',
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                bgcolor: 'transparent',
                color: 'primary.main',
              }
            }}
          >
            Payments
          </Button>
          <Button
            variant={tab === 3 ? 'text' : 'text'}
            onClick={() => { setTab(3); fetchAgencyFees(); }}
            sx={{
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: tab === 3 ? 800 : 700,
              color: tab === 3 ? 'primary.main' : 'text.secondary',
              minHeight: 48,
              px: 3,
              borderBottom: tab === 3 ? '3px solid' : 'none',
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                bgcolor: 'transparent',
                color: 'primary.main',
              }
            }}
          >
            Agency Fees
          </Button>
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Report Content Section */}
      <Box>
        {section === 0 && tab === 0 && (
          <Box>
            {renderFilterCard()}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
              Trial Balance shows all account balances at a point in time. Debits and credits must balance.
            </Typography>
            {loadingTB ? (
              <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
            ) : (
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper, variant: 'outlined' }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Debit</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Credit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trialBalance.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center">No data</TableCell></TableRow>
                )}
                {trialBalance.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.code}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontFamily: 'monospace',
                        color: Number(row.debit || 0) > 0 ? 'success.dark' : 'text.secondary',
                        fontWeight: Number(row.debit || 0) > 0 ? 500 : 400
                      }}
                    >
                      {formatAmount(row.debit || 0)}
                    </TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontFamily: 'monospace',
                        color: Number(row.credit || 0) > 0 ? 'info.dark' : 'text.secondary',
                        fontWeight: Number(row.credit || 0) > 0 ? 500 : 400
                      }}
                    >
                      {formatAmount(row.credit || 0)}
                    </TableCell>
                  </TableRow>
                ))}
                {trialBalance.length > 0 && (
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}><strong>Totals</strong></TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        color: 'success.dark'
                      }}
                    >
                      <strong>{formatAmount(trialBalance.reduce((a,b)=>a+Number(b.debit||0),0))}</strong>
                    </TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        color: 'info.dark'
                      }}
                    >
                      <strong>{formatAmount(trialBalance.reduce((a,b)=>a+Number(b.credit||0),0))}</strong>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </ScrollTable>
            )}
          </Box>
        )}

        {section === 0 && tab === 1 && (
          <Box>
            {renderFilterCard()}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
              Profit & Loss shows income and expenses for the selected period, calculating net operating income.
            </Typography>
            {loadingPL && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Total Income</Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'success.dark', fontWeight: 600 }}>
                    {formatCurrency(profitLoss.totalIncome || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Total Expenses</Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'error.dark', fontWeight: 600 }}>
                    {formatCurrency(profitLoss.totalExpense || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2" color="textSecondary">Net Income</Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                    {formatCurrency(profitLoss.netIncome || 0)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {section === 0 && tab === 2 && (
          <Box>
            {renderFilterCard()}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
              Cash Flow shows actual cash movements (inflows and outflows) from the Cash account (1000) for the selected period.
            </Typography>
            {loadingPay && loadingOcc && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Cash Inflow</Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'success.dark', fontWeight: 600 }}>
                    {formatCurrency(cashFlow.inflow || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Cash Outflow</Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'error.dark', fontWeight: 600 }}>
                    {formatCurrency(cashFlow.outflow || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2" color="textSecondary">Net Cash</Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                    {formatCurrency(cashFlow.net || 0)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Transactions Table */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Cash Flow Transactions</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper, variant: 'outlined' }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Inflow</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Outflow</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!cashFlow.transactions || cashFlow.transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No transactions found</TableCell>
                    </TableRow>
                  ) : (
                    cashFlow.transactions.map((transaction, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          {new Date(transaction.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{transaction.description}</TableCell>
                        <TableCell>{transaction.property}</TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ 
                            fontFamily: 'monospace',
                            color: transaction.type === 'inflow' ? 'success.dark' : 'text.secondary',
                            fontWeight: transaction.type === 'inflow' ? 500 : 400
                          }}
                        >
                          {transaction.type === 'inflow' ? formatAmount(transaction.amount) : '-'}
                        </TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ 
                            fontFamily: 'monospace',
                            color: transaction.type === 'outflow' ? 'error.dark' : 'text.secondary',
                            fontWeight: transaction.type === 'outflow' ? 500 : 400
                          }}
                        >
                          {transaction.type === 'outflow' ? formatAmount(transaction.amount) : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </ScrollTable>
          </Box>
        )}

        {section === 1 && tab === 0 && (
        <Box>
          {renderFilterCard(true)}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
            Occupancy report shows unit availability and utilization across all properties.
          </Typography>
          {loadingOcc && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}><Paper sx={{ p:2 }}><Typography variant="subtitle2" color="textSecondary">Total</Typography><Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{occupancy.total}</Typography></Paper></Grid>
            <Grid item xs={12} md={3}><Paper sx={{ p:2 }}><Typography variant="subtitle2" color="textSecondary">Occupied</Typography><Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'success.dark', fontWeight: 600 }}>{occupancy.occupied}</Typography></Paper></Grid>
            <Grid item xs={12} md={3}><Paper sx={{ p:2 }}><Typography variant="subtitle2" color="textSecondary">Available</Typography><Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'info.dark', fontWeight: 600 }}>{occupancy.available}</Typography></Paper></Grid>
            <Grid item xs={12} md={3}><Paper sx={{ p:2 }}><Typography variant="subtitle2" color="textSecondary">Maintenance</Typography><Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'warning.dark', fontWeight: 600 }}>{occupancy.maintenance}</Typography></Paper></Grid>
          </Grid>
          <Box mt={2}><Typography variant="subtitle2" color="textSecondary">Occupancy Rate</Typography><Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{occupancy.occupancyRate}%</Typography></Box>
          <Box mt={2}><ButtonGroup><Button variant="outlined" onClick={async () => {
            try {
              const res = await axios.get('/reports/occupancy', { params: { format: 'pdf' }, responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
              const link = document.createElement('a');
              link.href = url;
              link.download = 'occupancy_report.pdf';
              link.click();
              window.URL.revokeObjectURL(url);
            } catch(e) { toast.error('Export failed'); }
          }}>Export PDF</Button></ButtonGroup></Box>
        </Box>
      )}

        {section === 1 && tab === 1 && (
        <Box>
          {renderFilterCard(true)}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
            Repairs report shows maintenance request statistics by status and individual repair records for the selected period.
          </Typography>
          {loadingRep && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
          {!loadingRep && (
            <>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>Total Requests: <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{repairsRpt.total}</Box></Typography>
              
              {/* Summary by Status */}
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Summary by Status</Typography>
              <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper, variant: 'outlined', sx: { mb: 3 } }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(repairsRpt.byStatus || {}).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center">No data</TableCell>
                      </TableRow>
                    ) : (
                      Object.keys(repairsRpt.byStatus || {}).map(k => (
                        <TableRow key={k}>
                          <TableCell>{k}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{repairsRpt.byStatus[k]}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </ScrollTable>

              {/* Individual Repair Records */}
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Repair Records</Typography>
              <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper, variant: 'outlined' }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Property</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!repairsRpt.records || repairsRpt.records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">No repair records found</TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {repairsRpt.records.map((record) => (
                          <TableRow key={record._id}>
                            <TableCell>{record.title}</TableCell>
                            <TableCell>{record.property}</TableCell>
                            <TableCell>
                              <Chip 
                                label={record.status} 
                                size="small"
                                color={
                                  record.status === 'completed' ? 'success' :
                                  record.status === 'in_progress' ? 'warning' :
                                  record.status === 'cancelled' ? 'default' : 'info'
                                }
                              />
                            </TableCell>
                            <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                              {formatCurrency(record.cost || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell colSpan={4} align="right" sx={{ fontWeight: 700 }}>
                            <strong>Total Cost</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                            <strong>
                              {formatCurrency(
                                repairsRpt.records.reduce((sum, record) => sum + (record.cost || 0), 0)
                              )}
                            </strong>
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </ScrollTable>
            </>
          )}
          <Box mt={2}><Button variant="outlined" onClick={async () => {
            try {
              const res = await axios.get('/reports/repairs', { params: { ...buildParams(), format: 'pdf' }, responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
              const link = document.createElement('a');
              link.href = url;
              link.download = 'repairs_report.pdf';
              link.click();
              window.URL.revokeObjectURL(url);
            } catch(e) { toast.error('Export failed'); }
          }}>Export PDF</Button></Box>
        </Box>
      )}

        {section === 1 && tab === 2 && (
        <Box>
          {renderFilterCard()}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
            Payments report shows payment statistics and totals by status for the selected period.
          </Typography>
          {loadingPay && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><Paper sx={{ p:2 }}><Typography variant="subtitle2" color="textSecondary">Total Payments</Typography><Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{paymentsRpt.totalCount}</Typography></Paper></Grid>
            <Grid item xs={12} md={6}><Paper sx={{ p:2 }}><Typography variant="subtitle2" color="textSecondary">Total Amount</Typography><Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'success.dark' }}>{formatCurrency(paymentsRpt.totalAmount||0)}</Typography></Paper></Grid>
          </Grid>
          <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper, variant: 'outlined', sx: { mt: 2 } }} size="small">
            <TableHead><TableRow><TableCell>Status</TableCell><TableCell align="right" sx={{ fontFamily: 'monospace' }}>Count</TableCell><TableCell align="right" sx={{ fontFamily: 'monospace' }}>Total Amount</TableCell></TableRow></TableHead>
            <TableBody>
              {Object.keys(paymentsRpt.byStatus || {}).map(k => (
                <TableRow key={k}>
                  <TableCell>{k}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{paymentsRpt.byStatus[k].count}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{formatCurrency(paymentsRpt.byStatus[k].totalAmount||0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
          <Box mt={2}><Button variant="outlined" onClick={async () => {
            try {
              const res = await axios.get('/reports/payments', { params: { ...buildParams(), format: 'pdf' }, responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
              const link = document.createElement('a');
              link.href = url;
              link.download = 'payments_report.pdf';
              link.click();
              window.URL.revokeObjectURL(url);
            } catch(e) { toast.error('Export failed'); }
          }}>Export PDF</Button></Box>
        </Box>
        )}

        {section === 1 && tab === 3 && (
        <Box>
          {renderFilterCard()}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
            Agency Fees report shows management fee revenue by landlord for the selected period.
          </Typography>
          {loadingAgencyFees && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
          {!loadingAgencyFees && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" color="textSecondary">Total Revenue</Typography>
                    <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'success.dark' }}>
                      {formatCurrency(agencyFees.totalRevenue || 0)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Total Landlords</Typography>
                    <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {agencyFees.byLandlord?.length || 0}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Agency Fees by Landlord</Typography>
              <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper, variant: 'outlined' }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Landlord</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Properties</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Transactions</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Total Fees</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!agencyFees.byLandlord || agencyFees.byLandlord.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">No data found</TableCell>
                      </TableRow>
                    ) : (
                      agencyFees.byLandlord.map((landlord, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{landlord.landlordName}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{landlord.propertyCount || 0}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{landlord.transactionCount || 0}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'success.dark' }}>
                            {formatCurrency(landlord.totalFees || 0)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </ScrollTable>
            </>
          )}
        </Box>
        )}
      </Box>
    </Box>
  );
};

export default Reports;


